/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import {
  DEFAULT_INDUSTRY_TYPE,
  INDUSTRY_DEFINITIONS,
  type IndustryDefinition,
  type IndustryType,
} from "@/lib/constants/industries";
import {
  getIndustryTerminology,
  type IndustryTerminology,
} from "@/lib/constants/terminology";

// Re-export IndustryType for external use
export type { IndustryType };

export interface IndustryConfig {
  type: IndustryType;
  definition: IndustryDefinition;
  terminology: IndustryTerminology;
}

export interface PersistedIndustryConfig {
  type: IndustryType;
  terminology: IndustryTerminology;
  features: Record<string, unknown>;
  aiPersonality: Record<string, unknown>;
}

/**
 * Resolve an industry type from a raw DB/API value.
 * The DB enum now matches IndustryType values directly.
 */
export function resolveIndustryType(
  rawValue: string | null | undefined,
): IndustryType {
  if (!rawValue) {
    return DEFAULT_INDUSTRY_TYPE;
  }

  const normalized = rawValue.toString().trim();
  const value = normalized as IndustryType;
  const exists = INDUSTRY_DEFINITIONS.some(
    (definition) => definition.value === value,
  );
  if (exists) {
    return value;
  }

  return DEFAULT_INDUSTRY_TYPE;
}

export function getIndustryConfig(
  industry?: IndustryType | null,
): IndustryConfig {
  const type = industry ?? DEFAULT_INDUSTRY_TYPE;
  const definition =
    INDUSTRY_DEFINITIONS.find((item) => item.value === type) ??
    INDUSTRY_DEFINITIONS.find((item) => item.value === DEFAULT_INDUSTRY_TYPE)!;

  return {
    type,
    definition,
    terminology: getIndustryTerminology(type),
  };
}

export function buildInitialIndustryConfig(
  industryType: IndustryType,
): PersistedIndustryConfig {
  return {
    type: industryType,
    terminology: getIndustryTerminology(industryType),
    features: {},
    aiPersonality: {},
  };
}
